import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Download, Mail, Linkedin, Phone, Sparkles, Cpu, Cog, Zap, GitBranch, Github } from 'lucide-react';
import { personalInfo } from '@/data/portfolioConfig';
import uiConfig from '@/../../../config/ui.config.json';

// ─── Terminal live counter ──────────────────────────────────────────────────
function TerminalWindow() {
  const [step, setStep] = useState(14382);
  const [simTime, setSimTime] = useState(14.382);
  const filled = 11; // static progress bar fill out of 16 chars ≈ 71%

  useEffect(() => {
    const id = setInterval(() => {
      setStep(s => s + Math.floor(Math.random() * 4 + 1));
      setSimTime(t => parseFloat((t + (Math.random() * 0.004 + 0.001)).toFixed(3)));
    }, 80);
    return () => clearInterval(id);
  }, []);

  const bar = '█'.repeat(filled) + '░'.repeat(16 - filled);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 2.8, duration: 0.8, ease: 'easeOut' }}
      className="absolute bottom-20 right-6 xl:right-10 hidden xl:block z-20"
    >
      <div
        className="w-72 rounded-xl overflow-hidden border border-primary/25 shadow-2xl shadow-primary/20 backdrop-blur-xl"
        style={{ background: 'rgba(3,7,18,0.85)' }}
      >
        {/* Mac dots header */}
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/5" style={{ background: 'rgba(10,10,20,0.9)' }}>
          <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
          <span className="ml-2 font-mono text-[10px] text-gray-500">fmu-runner-silkit</span>
        </div>
        {/* Output */}
        <div className="px-3 py-3 font-mono text-[11px] space-y-1.5">
          <div className="text-gray-500">$ ./fmu-runner --fmu engine.fmu</div>
          <div className="text-primary/80">✓ FMI 3.0 · 47 variables loaded</div>
          <div className="text-primary/80">✓ SIL-Kit → registry:8500</div>
          <div className="text-primary/80">✓ dt=0.001s · CoSimulation</div>
          <div className="flex items-center gap-1.5 text-green-400/90 tabular-nums">
            <motion.span animate={{ opacity: [1, 0, 1] }} transition={{ duration: 1, repeat: Infinity }}>■</motion.span>
            <span>t={simTime.toFixed(3)}s</span>
            <span className="text-gray-600 mx-0.5">|</span>
            <span>step={step.toLocaleString()}</span>
          </div>
          <div className="text-cyan-400/60 text-[10px] tabular-nums tracking-tight">{bar} 71%</div>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Floating tech label cloud ─────────────────────────────────────────────
const TECH_BADGES = [
  { label: 'C++23',    x: '6%',  y: '28%', delay: 0,   dur: 13 },
  { label: 'FMI 3.0', x: '79%', y: '22%', delay: 1.3, dur: 11 },
  { label: 'Qt6',     x: '10%', y: '63%', delay: 0.7, dur: 14 },
  { label: 'ASAM MDF',x: '82%', y: '60%', delay: 2.1, dur: 10 },
  { label: 'SIL-Kit', x: '76%', y: '81%', delay: 0.4, dur: 12 },
  { label: 'Python',  x: '14%', y: '80%', delay: 1.8, dur: 9  },
  { label: 'CMake',   x: '87%', y: '40%', delay: 1.0, dur: 15 },
  { label: 'Docker',  x: '3%',  y: '46%', delay: 2.4, dur: 11 },
  { label: 'AUTOSAR', x: '37%', y: '91%', delay: 0.5, dur: 13 },
  { label: 'FMU',     x: '61%', y: '11%', delay: 2.0, dur: 8  },
  { label: 'MDF4',    x: '24%', y: '14%', delay: 0.9, dur: 10 },
  { label: 'OpenSSL', x: '53%', y: '94%', delay: 2.7, dur: 12 },
];

// ─── Component ─────────────────────────────────────────────────────────────
export default function HeroNew() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'it' | 'en';
  const { scrollY } = useScroll();

  const y1 = useTransform(scrollY, [0, 300], [0, -50]);
  const y2 = useTransform(scrollY, [0, 300], [0, 50]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.2 } }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: 'spring' as const, stiffness: 100, damping: 12 } }
  };

  return (
    <section className="min-h-screen flex items-center justify-center relative overflow-hidden bg-surface dark:bg-dark-bg tech-grid-bg">

      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-10 dark:opacity-25"
        style={{ backgroundImage: `url('${uiConfig.hero.backgroundImage}')` }}
      />

      {/* Gradient mesh */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 dark:from-black/60 dark:via-black/30 dark:to-black/60" />

      {/* Scanline - dark mode only */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden hidden dark:block z-10">
        <motion.div
          className="absolute left-0 right-0 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(22,255,187,0.35), transparent)' }}
          animate={{ y: ['-8px', '100vh'] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'linear', delay: 1.5 }}
        />
      </div>

      {/* Perspective floor grid - dark mode */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none hidden dark:block overflow-hidden" style={{ height: '200px' }}>
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage: 'linear-gradient(rgba(22,255,187,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(22,255,187,0.8) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
            transform: 'perspective(280px) rotateX(68deg)',
            transformOrigin: 'bottom center',
          }}
        />
        {/* Fade out top of grid */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, var(--tw-bg-opacity,1) 0%, transparent 60%)' }} />
      </div>

      {/* Animated blobs with parallax */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          style={{ y: y1 }}
          className="absolute w-96 h-96 -top-48 -left-48 bg-gradient-to-br from-emerald-500/20 via-primary/10 to-transparent rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          style={{ y: y2 }}
          className="absolute w-96 h-96 -bottom-48 -right-48 bg-gradient-to-tl from-cyan-500/20 via-secondary/10 to-transparent rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], rotate: [90, 0, 90] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute w-64 h-64 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary/5 rounded-full blur-2xl"
          animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Floating particles */}
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-primary/40 rounded-full"
            style={{ left: `${(i * 7.3) % 100}%`, top: `${(i * 13.7) % 100}%` }}
            animate={{ y: [0, -30, 0], opacity: [0, 1, 0] }}
            transition={{ duration: 3 + (i % 3), repeat: Infinity, delay: (i * 0.2) % 2.5, ease: 'easeInOut' }}
          />
        ))}
      </div>

      {/* Floating tech icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[
          { Icon: Cpu,       x: '10%', y: '20%', delay: 0,   dur: 8  },
          { Icon: Cog,       x: '85%', y: '15%', delay: 1,   dur: 10 },
          { Icon: Zap,       x: '15%', y: '70%', delay: 0.5, dur: 7  },
          { Icon: GitBranch, x: '80%', y: '75%', delay: 1.5, dur: 9  },
        ].map(({ Icon, x, y, delay, dur }, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{ left: x, top: y }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 0.12, 0], scale: [0.8, 1.2, 0.8], rotate: [0, 180, 360] }}
            transition={{ duration: dur, repeat: Infinity, delay, ease: 'easeInOut' }}
          >
            <Icon className="h-16 w-16 sm:h-24 sm:w-24 text-primary" />
          </motion.div>
        ))}
      </div>

      {/* ── Floating tech badge cloud (desktop) ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden lg:block z-[5]">
        {TECH_BADGES.map(({ label, x, y, delay, dur }) => (
          <motion.div
            key={label}
            className="absolute"
            style={{ left: x, top: y }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.65, 0.45, 0.65, 0], y: [0, -14, 0, 10, 0], x: [0, 4, -4, 0] }}
            transition={{ duration: dur, repeat: Infinity, delay, ease: 'easeInOut' }}
          >
            <span className="font-mono text-[11px] font-bold text-gray-500/70 dark:text-primary/55 px-2.5 py-1 rounded-md border border-gray-300/40 dark:border-primary/25 bg-white/30 dark:bg-primary/5 backdrop-blur-sm">
              {label}
            </span>
          </motion.div>
        ))}
      </div>

      {/* ── Terminal window (xl desktop only) ── */}
      <TerminalWindow />

      {/* ── Main content ── */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-16 md:pb-20 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-center"
        >
          {/* Greeting */}
          <motion.div variants={itemVariants} className="flex items-center justify-center gap-2 mb-3 sm:mb-4 w-full px-4">
            <motion.div
              animate={{ rotate: [0, 14, -8, 14, -4, 10, 0] }}
              transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 1 }}
            >
              <Sparkles className="h-5 w-5 text-primary" />
            </motion.div>
            <p className="text-primary font-bold text-base sm:text-lg whitespace-nowrap">
              {t('hero.greeting')}
            </p>
          </motion.div>

          {/* Name */}
          <motion.h1
            variants={itemVariants}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-3 sm:mb-4 px-2"
            style={{ textShadow: '0 2px 10px rgba(0,0,0,0.7), 0 4px 20px rgba(0,0,0,0.5)' }}
          >
            <motion.span
              className="text-gradient inline-block"
              whileHover={{ scale: 1.05 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              {personalInfo.name}
            </motion.span>
          </motion.h1>

          {/* Title */}
          <motion.h2
            variants={itemVariants}
            className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-text-light mb-4 sm:mb-6 px-4 drop-shadow-lg"
          >
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              transition={{ duration: 1, delay: 0.8 }}
              className="inline-block px-4 py-2 bg-white/90 dark:bg-transparent backdrop-blur-md rounded-2xl border-2 border-white/50 dark:border-transparent shadow-xl dark:shadow-none"
            >
              {personalInfo.title[currentLang]}
            </motion.span>
          </motion.h2>

          {/* Subtitle */}
          <motion.p
            variants={itemVariants}
            className="text-base sm:text-lg md:text-xl text-gray-800 dark:text-text-light max-w-3xl mx-auto mb-6 sm:mb-8 px-4"
          >
            <span className="inline-block px-6 py-4 bg-white/95 dark:bg-transparent backdrop-blur-lg rounded-2xl border-2 border-white/60 dark:border-transparent shadow-2xl dark:shadow-none font-semibold leading-relaxed">
              {personalInfo.subtitle[currentLang]}
            </span>
          </motion.p>

          {/* Stats */}
          <motion.div
            variants={itemVariants}
            className="flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-8 mb-6 sm:mb-8 px-4"
          >
            {uiConfig.hero.stats.map((stat, index) => {
              const gradients = [
                'from-primary to-secondary',
                'from-secondary to-primary',
                'from-primary via-cyan-400 to-secondary',
                'from-cyan-500 to-primary'
              ];
              return (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.15, y: -8 }}
                  whileTap={{ scale: 0.95 }}
                  className="group flex flex-col items-center gap-1 px-5 py-4 bg-white/80 dark:bg-dark-bg-elevated backdrop-blur-xl rounded-2xl border-2 border-white/40 dark:border-primary/30 dark:hover:border-primary shadow-2xl dark:shadow-primary/10 dark:hover:shadow-primary/30 transition-all"
                >
                  <span className={`text-2xl sm:text-3xl md:text-4xl font-black font-mono bg-gradient-to-r ${gradients[index % gradients.length]} bg-clip-text text-transparent`}>
                    {stat.value}
                  </span>
                  <span className="text-xs sm:text-sm text-gray-800 dark:text-text-light font-bold text-center dark:group-hover:text-primary transition-colors">
                    {stat.label[currentLang]}
                  </span>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Contact quick links */}
          <motion.div
            variants={itemVariants}
            className="flex flex-wrap justify-center gap-2 sm:gap-3 md:gap-4 mb-6 sm:mb-8 px-4"
          >
            {[
              { icon: Mail,     text: personalInfo.email,  shortText: 'Email',  href: `mailto:${personalInfo.email}` },
              { icon: Linkedin, text: 'LinkedIn',           href: personalInfo.linkedin, external: true },
              { icon: Github,   text: 'GitHub',             href: personalInfo.github, external: true },
              { icon: Phone,    text: personalInfo.phone,   shortText: 'Chiama', href: `tel:${personalInfo.phone}` }
            ].map((item, index) => (
              <motion.a
                key={index}
                href={item.href}
                target={item.external ? '_blank' : undefined}
                rel={item.external ? 'noopener noreferrer' : undefined}
                whileHover={{ scale: 1.08, y: -4 }}
                whileTap={{ scale: 0.95 }}
                className="group inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl bg-white/90 dark:bg-dark-bg-elevated backdrop-blur-lg border-2 border-gray-200 dark:border-primary/25 hover:border-primary dark:hover:border-primary hover:shadow-xl hover:shadow-primary/30 dark:hover:shadow-primary/30 transition-all text-gray-800 dark:text-primary text-sm sm:text-base font-semibold"
              >
                <item.icon className="h-4 w-4 flex-shrink-0 group-hover:rotate-12 group-hover:scale-125 transition-all" />
                <span className="hidden md:inline truncate">{item.text}</span>
                {item.shortText && <span className="md:hidden">{item.shortText}</span>}
              </motion.a>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 px-4"
          >
            <motion.button
              onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              whileHover={{ scale: 1.1, boxShadow: '0 20px 60px rgba(22,255,187,0.7)' }}
              whileTap={{ scale: 0.95 }}
              className="group relative inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-4 sm:py-5 bg-gradient-to-r from-primary to-secondary text-white dark:text-gray-900 font-black rounded-2xl overflow-hidden shadow-2xl shadow-primary/60 text-sm sm:text-base hover:shadow-primary/80 transition-all"
            >
              <motion.span
                className="absolute inset-0 bg-gradient-to-r from-secondary to-primary"
                initial={{ x: '100%' }}
                whileHover={{ x: '0%' }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              />
              <span className="relative z-10 flex items-center gap-2">
                <motion.span animate={{ textShadow: ['0 0 8px rgba(255,255,255,0.5)', '0 0 12px rgba(255,255,255,0.8)', '0 0 8px rgba(255,255,255,0.5)'] }} transition={{ duration: 2, repeat: Infinity }}>
                  {t('hero.viewPortfolio')}
                </motion.span>
              </span>
              <motion.div animate={{ x: [0, 6, 0] }} transition={{ duration: 1.5, repeat: Infinity }} className="relative z-10">
                <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6 group-hover:rotate-45 transition-transform" />
              </motion.div>
            </motion.button>

            <motion.a
              href={personalInfo.cvDownloadUrl}
              download
              whileHover={{ scale: 1.08, y: -4 }}
              whileTap={{ scale: 0.95 }}
              className="group inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-4 sm:py-5 border-2 border-primary bg-white/90 dark:bg-dark-bg-elevated text-primary font-black rounded-2xl hover:bg-gradient-to-r hover:from-primary hover:to-secondary hover:text-white dark:hover:text-gray-900 hover:border-transparent transition-all backdrop-blur-lg shadow-xl hover:shadow-2xl hover:shadow-primary/60 text-sm sm:text-base"
            >
              <Download className="h-5 w-5 sm:h-6 sm:w-6 group-hover:animate-bounce" />
              {t('about.downloadCV')}
            </motion.a>
          </motion.div>

          {/* Scroll indicator */}
          <motion.button
            onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.5 }}
            whileHover={{ scale: 1.2 }}
            className="mt-4 sm:mt-6 md:mt-8 hidden sm:block mx-auto cursor-pointer group"
            aria-label="Scroll to content"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              className="w-6 h-10 border-2 border-primary/50 group-hover:border-primary rounded-full mx-auto flex items-start justify-center p-2 transition-colors"
            >
              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                className="w-1 h-2 bg-primary rounded-full shadow-lg shadow-primary/50"
              />
            </motion.div>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}
